import { IScopeClause } from "@/api";
import { IClauseComment } from "@/api/comments";
import { getCommentsByClauseId } from "@/api/scopes";
import { useAuth } from "@/contexts/auth/useAuth";
import { Box, Stack } from "@mui/material";
import { useMutationState, useQuery } from "@tanstack/react-query";
import { formatISO } from "date-fns";
import { useEffect, useState } from "react";
import { Comment } from "./Comment";

interface IProps {
    scopeClause: IScopeClause;
    /**
     * Whether the scope clause is expanded
     */
    expanded: boolean;
}

export const CommentsList = ({ scopeClause, expanded }: IProps) => {
    const { user } = useAuth();

    const variables = useMutationState<string>({
        filters: { mutationKey: ["addComment"], status: "pending" },
        select: (mutation) => mutation.state.variables as string,
    });

    const [optimisticValue, setOptimisticValue] = useState<string>("");

    useEffect(() => {
        if (variables.length > 0 && variables[0]) {
            setOptimisticValue(variables[0]);
        }
    }, [variables]);

    const { isFetching, error, data } = useQuery<{
        items: IClauseComment[];
    }>({
        queryKey: [`frontend-scope-clause-comments`, scopeClause.id],
        queryFn: () =>
            getCommentsByClauseId(scopeClause.scope_id, scopeClause.id),
        enabled: expanded,
    });

    if (!user) return null;
    if (error) return <div>Error: {error.message}</div>;
    if (!data) return null;
    return (
        <Stack
            spacing={2}
            justifyContent="space-between"
            mt={1}
            sx={{ maxHeight: "400px", overflowY: "auto" }}
        >
            {(variables.length > 0 || isFetching) && (
                <Comment
                    comment={{
                        id: 0,
                        created_at: formatISO(new Date()),
                        updated_at: formatISO(new Date()),
                        user_id: user?.id,
                        scope_clause_id: scopeClause.id,
                        user: user,
                        scope_clause: scopeClause,
                        comment: optimisticValue,
                    }}
                />
            )}
            {data.items.map((comment) => (
                <Box key={comment.id}>
                    <Comment comment={comment} />
                </Box>
            ))}
        </Stack>
    );
};
